import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  AttachMoney as AttachMoneyIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Divider,
  Button,
  useMediaQuery,
  useTheme,
  Avatar,
  Paper,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link, useNavigate } from 'react-router-dom';

import { useCustomerDetail } from '../../hooks/useCustomerDetail';
import { Customer, CustomerAddress, CustomerCommunication, CustomerContact } from '../../types/customer';
import { useCustomerProperties } from '../../hooks/useCustomerProperties';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface UseCustomerDetailParams {
  customerCode: string;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const CustomerView = () => {
  const params = useParams<{ customerCode: string }>();
  const customerCode = params.customerCode;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);

  // useCustomerDetail hook'unu kullanarak gerçek verileri al
  const { data: customerData, isLoading, error } = useCustomerDetail({ customerCode });

  // Müşteri özellikleri (tipler, değerler ve seçili özellikler)
  const { propertyTypes, propertyValues, customerProperties: selectedProps } = useCustomerProperties(customerCode || undefined);

  // API'den gelen verileri state'e aktar
  useEffect(() => {
    if (customerData) {
      setCustomer(customerData);
    }
  }, [customerData]);

  const [customer, setCustomer] = useState<Customer>({
    customerCode: '',
    customerName: '',
    customerTypeCode: 0,
    isActive: true,
    isBlocked: false,
    communications: [],
    addresses: [],
    contacts: []
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Bağlantılı kişiler render
  const renderContacts = () => {
    if (!customer.contacts || customer.contacts.length === 0) {
      return <Typography variant="body2">Bağlantılı kişi bulunamadı.</Typography>;
    }
    return (
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {customer.contacts.map((c: CustomerContact, idx: number) => (
          <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2">{c.contactName}</Typography>
              {c.isDefault && <Chip size="small" color="primary" label="Varsayılan" />}
            </Box>
            <Typography variant="body2" color="text.secondary">{c.contactTitle || '-'}</Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
              <Typography variant="body2">{c.contactPhone || '-'}</Typography>
              <Typography variant="body2">{c.contactEmail || '-'}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  // Müşteri Özellikleri render
  const renderProperties = () => {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 45%', minWidth: '160px' }}>
          <Typography variant="body2" color="text.secondary">Gerçek Kişi</Typography>
          <Typography variant="body1">{customer.isRealPerson ? 'Evet' : 'Hayır'}</Typography>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '160px' }}>
          <Typography variant="body2" color="text.secondary">Bloke</Typography>
          <Typography variant="body1">{customer.isBlocked ? 'Evet' : 'Hayır'}</Typography>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '160px' }}>
          <Typography variant="body2" color="text.secondary">E-Fatura</Typography>
          <Typography variant="body1">{customer.eInvoiceEnabled ? 'Evet' : 'Hayır'}</Typography>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '160px' }}>
          <Typography variant="body2" color="text.secondary">E-Arşiv</Typography>
          <Typography variant="body1">{customer.eArchiveEnabled ? 'Evet' : 'Hayır'}</Typography>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '160px' }}>
          <Typography variant="body2" color="text.secondary">Ülke</Typography>
          <Typography variant="body1">{customer.countryDescription || '-'}</Typography>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '160px' }}>
          <Typography variant="body2" color="text.secondary">Şehir</Typography>
          <Typography variant="body1">{customer.cityDescription || '-'}</Typography>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '160px' }}>
          <Typography variant="body2" color="text.secondary">İlçe</Typography>
          <Typography variant="body1">{customer.districtDescription || '-'}</Typography>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '160px' }}>
          <Typography variant="body2" color="text.secondary">Para Birimi</Typography>
          <Typography variant="body1">{customer.currencyCode || '-'}</Typography>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '160px' }}>
          <Typography variant="body2" color="text.secondary">Oluşturulma</Typography>
          <Typography variant="body1">{customer.createdAt ? new Date(customer.createdAt).toLocaleString('tr-TR') : '-'}</Typography>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '160px' }}>
          <Typography variant="body2" color="text.secondary">Güncellenme</Typography>
          <Typography variant="body1">{customer.modifiedAt ? new Date(customer.modifiedAt).toLocaleString('tr-TR') : '-'}</Typography>
        </Box>

        {/* Ortak Müşteri Özellikleri: Şirketler ve Markalar */
        }
        {(() => {
          // Yardımcı: bir typeCode için seçilen kod(lar)ı açıklamaya çevir
          const resolveValues = (typeCode: number, raw: string | undefined): string => {
            if (!raw) return '-';
            const list = propertyValues?.[typeCode] || [];
            // Virgülle çoklu seçim (özellikle Markalar)
            return raw
              .split(',')
              .map(code => {
                const item = list.find((v: any) => String(v.valueCode || v.code) === String(code).trim());
                const name = item ? (item.valueName || item.name) : undefined;
                return name ? `${code.trim()} - ${name}` : code.trim();
              })
              .join(', ');
          };

          const companiesRaw = selectedProps?.[2] as unknown as string | undefined; // Çalıştığı Şirketler
          const brandsRaw = selectedProps?.[3] as unknown as string | undefined;    // Çalıştığı Markalar

          const companiesText = resolveValues(2, companiesRaw);
          const brandsText = resolveValues(3, brandsRaw);

          return (
            <>
              <Divider sx={{ width: '100%', my: 1 }} />
              <Box sx={{ flex: '1 1 100%', minWidth: '200px' }}>
                <Typography variant="body2" color="text.secondary">Çalıştığı Şirketler</Typography>
                <Typography variant="body1">{companiesText}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 100%', minWidth: '200px' }}>
                <Typography variant="body2" color="text.secondary">Çalıştığı Markalar</Typography>
                <Typography variant="body1">{brandsText}</Typography>
              </Box>
            </>
          );
        })()}

        {/* Tüm Müşteri Özellikleri: Modal'daki tüm tipler ve seçili değerler */}
        {(() => {
          if (!propertyTypes || propertyTypes.length === 0) return null;
          const resolveSingle = (typeCode: number, raw: string | undefined): string => {
            if (!raw) return '-';
            const list = propertyValues?.[typeCode] || [];
            const found = list.find((v: any) => String(v.valueCode || v.code) === String(raw));
            if (!found) return raw;
            const code = found.valueCode || found.code;
            const name = found.valueName || found.name;
            return `${code} - ${name}`;
          };

          return (
            <>
              <Divider sx={{ width: '100%', my: 1 }} />
              <Box sx={{ flex: '1 1 100%', minWidth: '200px' }}>
                <Typography variant="subtitle2">Tüm Müşteri Özellikleri</Typography>
              </Box>
              {propertyTypes
                .filter((t: any) => (t.typeName || '').toLowerCase() !== 'eski kod')
                .map((t: any) => (
                  <Box key={t.typeCode} sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                    <Typography variant="body2" color="text.secondary">{t.typeName}</Typography>
                    <Typography variant="body1">
                      {(() => {
                        const raw = selectedProps?.[t.typeCode] as unknown as string | undefined;
                        // Çoklu seçim olasılığı (virgüllü)
                        if (raw && raw.includes(',')) {
                          return raw.split(',').map(code => resolveSingle(t.typeCode, code.trim())).join(', ');
                        }
                        return resolveSingle(t.typeCode, raw);
                      })()}
                    </Typography>
                  </Box>
                ))}
            </>
          );
        })()}
      </Box>
    );
  };

  const handleEdit = () => {
    navigate(`/customers/edit/${customerCode}`);
  };

  // Mobil görünüm için iletişim bilgilerini render eden fonksiyon
  const renderCommunications = () => {
    const phones = (customer.communications || []).filter(
      (c: CustomerCommunication) => (c.communicationTypeCode || '').toUpperCase().includes('PHONE') || (c.communicationTypeCode || '').toUpperCase() === 'TEL'
    );
    const emails = (customer.communications || []).filter(
      (c: CustomerCommunication) => (c.communicationTypeCode || '').toUpperCase().includes('EMAIL') || (c.communicationTypeCode || '').toUpperCase() === 'MAIL'
    );

    return (
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>Telefonlar</Typography>
          {phones.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Telefon bulunamadı.</Typography>
          ) : (
            phones.map((comm: CustomerCommunication, index: number) => (
              <Box key={`phone-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PhoneIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2" component="div">
                  {comm.communication || '-'}
                  {comm.isDefault && <Chip size="small" label="Varsayılan" color="primary" sx={{ ml: 1, height: 20 }} />}
                </Typography>
              </Box>
            ))
          )}
        </Box>
        <Divider />
        <Box>
          <Typography variant="subtitle2" gutterBottom>E-posta Adresleri</Typography>
          {emails.length === 0 ? (
            <Typography variant="body2" color="text.secondary">E-posta bulunamadı.</Typography>
          ) : (
            emails.map((comm: CustomerCommunication, index: number) => (
              <Box key={`email-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2" component="div">
                  {comm.communication || '-'}
                  {comm.isDefault && <Chip size="small" label="Varsayılan" color="primary" sx={{ ml: 1, height: 20 }} />}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Box>
    );
  };

  // Mobil görünüm için adres bilgilerini render eden fonksiyon
  const renderAddresses = () => {
    if (!customer.addresses || customer.addresses.length === 0) {
      return <Typography variant="body2">Adres bilgisi bulunamadı.</Typography>;
    }
    
    return (
      <Box sx={{ mt: 2 }}>
        {[...customer.addresses].sort((a: CustomerAddress, b: CustomerAddress) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0)).map((addr: CustomerAddress, index: number) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <LocationIcon fontSize="small" color="primary" sx={{ mr: 1, mt: 0.5 }} />
            <Box>
              <Typography variant="body2" component="div" sx={{ fontWeight: addr.isDefault ? 'bold' : 'normal' }}>
                {addr.addressType || addr.addressTypeCode}
                {addr.isDefault && <Chip size="small" label="Varsayılan" color="primary" sx={{ ml: 1, height: 20 }} />}
              </Typography>
              <Typography variant="body2">
                {addr.address}
                {addr.districtDescription || addr.district ? `, ${addr.districtDescription || addr.district}` : ''}
                {addr.cityDescription || addr.city ? `, ${addr.cityDescription || addr.city}` : ''}
                {addr.countryDescription || addr.country ? `, ${addr.countryDescription || addr.country}` : ''}
                {addr.postalCode ? ` (${addr.postalCode})` : ''}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ pb: 8 }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Yükleniyor...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ pb: 8 }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Hata oluştu: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      {/* Mobil görünüm */}
      {isMobile ? (
        <Box>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
            <IconButton component={Link} to="/customers" edge="start" color="inherit" aria-label="back">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 1 }}>
              Müşteri Detayı
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton color="primary" onClick={handleEdit}>
              <EditIcon />
            </IconButton>
          </Box>

          <Card sx={{ mx: 2, mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: customer.isRealPerson ? 'primary.main' : 'secondary.main', mr: 2 }}>
                  {customer.isRealPerson ? <PersonIcon /> : <BusinessIcon />}
                </Avatar>
                <Box>
                  <Typography variant="h6">{customer.customerName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {customer.customerCode}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" color="text.secondary">
                Müşteri Tipi
              </Typography>
              <Typography variant="body1" gutterBottom>
                {customer.isRealPerson ? "Bireysel" : "Kurumsal"}
              </Typography>
              
              <Typography variant="subtitle2" color="text.secondary">
                Vergi/TC No
              </Typography>
              <Typography variant="body1" gutterBottom>
                {customer.isRealPerson ? customer.identityNum : customer.taxNumber}
              </Typography>
              
              <Typography variant="subtitle2" color="text.secondary">
                Vergi Dairesi
              </Typography>
              <Typography variant="body1" gutterBottom>
                {customer.taxOffice || "-"}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                İletişim Bilgileri
              </Typography>
              {renderCommunications()}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Adres Bilgileri
              </Typography>
              {renderAddresses()}
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">
                  Bakiye
                </Typography>
                <Typography 
                  variant="h6" 
                  color={customer.balance && customer.balance < 0 ? "error.main" : "success.main"}
                >
                  {customer.balance !== undefined 
                    ? customer.balance.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })
                    : "-"}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Kredi Limiti
                </Typography>
                <Typography variant="body1">
                  {customer.creditLimit !== undefined 
                    ? customer.creditLimit.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })
                    : "-"}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Risk
                </Typography>
                <Typography variant="body1">
                  {customer.totalRisk !== undefined 
                    ? customer.totalRisk.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })
                    : "-"}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      ) : (
        // Masaüstü görünüm
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button
              component={Link}
              to="/customers"
              startIcon={<ArrowBackIcon />}
              sx={{ mr: 2 }}
            >
              Müşteri Listesi
            </Button>
            <Typography variant="h5">{customer.customerName}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Düzenle
            </Button>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="customer tabs">
              <Tab label="Müşteri Bilgileri" id="customer-tab-0" aria-controls="customer-tabpanel-0" />
              <Tab label="İletişim" id="customer-tab-1" aria-controls="customer-tabpanel-1" />
              <Tab label="Adresler" id="customer-tab-2" aria-controls="customer-tabpanel-2" />
              <Tab label="Bağlantılı Kişiler" id="customer-tab-3" aria-controls="customer-tabpanel-3" />
              <Tab label="Müşteri Özellikleri" id="customer-tab-4" aria-controls="customer-tabpanel-4" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Müşteri Bilgileri</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 45%', minWidth: '120px' }}>
                        <Typography variant="body2" color="text.secondary">Müşteri Tipi</Typography>
                        <Typography variant="body1">{customer.isRealPerson ? "Bireysel" : "Kurumsal"}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 45%', minWidth: '120px' }}>
                        <Typography variant="body2" color="text.secondary">Vergi Dairesi</Typography>
                        <Typography variant="body1">{customer.taxOffice || "-"}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 45%', minWidth: '120px' }}>
                        <Typography variant="body2" color="text.secondary">Vergi/TC No</Typography>
                        <Typography variant="body1">{customer.isRealPerson ? customer.identityNum : customer.taxNumber}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 45%', minWidth: '120px' }}>
                        <Typography variant="body2" color="text.secondary">Ödeme Vadesi</Typography>
                        <Typography variant="body1">{customer.paymentTerm || 0} gün</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Finansal Bilgiler</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 45%', minWidth: '120px' }}>
                        <Typography variant="body2" color="text.secondary">Bakiye</Typography>
                        <Typography variant="body1" color={customer.balance && customer.balance < 0 ? "error.main" : "success.main"}>
                          {customer.balance !== undefined 
                            ? customer.balance.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })
                            : "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 45%', minWidth: '120px' }}>
                        <Typography variant="body2" color="text.secondary">Kredi Limiti</Typography>
                        <Typography variant="body1">
                          {customer.creditLimit !== undefined 
                            ? customer.creditLimit.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })
                            : "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 45%', minWidth: '120px' }}>
                        <Typography variant="body2" color="text.secondary">Risk</Typography>
                        <Typography variant="body1">
                          {customer.totalRisk !== undefined 
                            ? customer.totalRisk.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })
                            : "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>İletişim Bilgileri</Typography>
                {(() => {
                  const phones = (customer.communications || []).filter(
                    (c: CustomerCommunication) => (c.communicationTypeCode || '').toUpperCase().includes('PHONE') || (c.communicationTypeCode || '').toUpperCase() === 'TEL'
                  );
                  const emails = (customer.communications || []).filter(
                    (c: CustomerCommunication) => (c.communicationTypeCode || '').toUpperCase().includes('EMAIL') || (c.communicationTypeCode || '').toUpperCase() === 'MAIL'
                  );
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>Telefonlar</Typography>
                        {phones.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">Telefon bulunamadı.</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {phones.map((comm: CustomerCommunication, index: number) => (
                              <Box key={`phone-${index}`} sx={{ flex: '1 1 45%', minWidth: '160px', display: 'flex', alignItems: 'center' }}>
                                <PhoneIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                                <Typography variant="body1">{comm.communication || '-'}</Typography>
                                {comm.isDefault && <Chip size="small" label="Varsayılan" color="primary" sx={{ ml: 1, height: 20 }} />}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>E-posta Adresleri</Typography>
                        {emails.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">E-posta bulunamadı.</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {emails.map((comm: CustomerCommunication, index: number) => (
                              <Box key={`email-${index}`} sx={{ flex: '1 1 45%', minWidth: '160px', display: 'flex', alignItems: 'center' }}>
                                <EmailIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                                <Typography variant="body1">{comm.communication || '-'}</Typography>
                                {comm.isDefault && <Chip size="small" label="Varsayılan" color="primary" sx={{ ml: 1, height: 20 }} />}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Adresler</Typography>
                {customer.addresses?.sort((a, b) => (a.isDefault ? -1 : 1)).map((address: CustomerAddress, index: number) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1">{address.address}</Typography>
                    {address.isDefault && <Chip size="small" label="Varsayılan" color="primary" sx={{ ml: 1, height: 20 }} />}
                  </Box>
                ))}
                {renderAddresses()}
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Bağlantılı Kişiler</Typography>
                {renderContacts()}
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Müşteri Özellikleri</Typography>
                {renderProperties()}
              </CardContent>
            </Card>
          </TabPanel>
        </Box>
      )}
    </Box>
  );
};

export default CustomerView;
